import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@auth";
import { createUiPreference, deleteUiPreference, updateUiPreference } from "@api";
import { toast } from "@components/toast/Toast";
import { WORKING_SET_NAME_MAX, WORKING_SET_TYPE, firstFreeSlot, moveToSlot, placeWorkingSets, serializeWorkingSet } from "./workingSets";

// Every working-set write goes through here. Each one patches the shared uiPreferences cache as
// soon as the server accepts it, so cards, the Trade Accounts title and the builder all reflect
// the change at once instead of waiting on the refetch the API helpers trigger.
export const useWorkingSetActions = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const patchCache = (updater) =>
    queryClient.setQueryData(["uiPreferences", user?.guid], (old) => updater(old ?? []));

  const create = async ({ name, icon, colour, filters, order = null }) => {
    const created = await createUiPreference({
      name,
      type: WORKING_SET_TYPE,
      value: serializeWorkingSet({ icon, colour, order, filters }),
    });
    if (!created) {
      toast({ description: "Couldn't create the working set. Please try again." });
      return null;
    }
    patchCache((old) => [...old.filter((p) => p.id !== created.id), created]);
    return created;
  };

  const update = async (
    set,
    { name = set.name, icon = set.icon, colour = set.colour, filters = set.filters, order = set.order } = {},
  ) => {
    const value = serializeWorkingSet({ icon, colour, order, filters });
    const ok = await updateUiPreference(set.id, { name, value });
    if (!ok) {
      toast({ description: `Couldn't update "${set.name}". Please try again.` });
      return false;
    }
    patchCache((old) => old.map((p) => (p.id === set.id ? { ...p, name, value } : p)));
    return true;
  };

  const remove = async (set) => {
    const ok = await deleteUiPreference(set.id);
    if (!ok) {
      toast({ description: `Couldn't remove "${set.name}". Please try again.` });
      return false;
    }
    patchCache((old) => old.filter((p) => p.id !== set.id));
    toast({ description: `Removed "${set.name}"` });
    return true;
  };

  const duplicate = async (set, sets) => {
    const slot = firstFreeSlot(sets);
    if (slot === null) {
      toast({ description: `Your board is full. Remove a working set to make room for another.` });
      return null;
    }
    const name = `${set.name} copy`.slice(0, WORKING_SET_NAME_MAX);
    const created = await create({ name, icon: set.icon, colour: set.colour, filters: set.filters, order: slot });
    if (created) toast({ description: `Duplicated as "${name}"` });
    return created;
  };

  // Drops a set into a board slot: an empty slot just takes it, an occupied one swaps the two sets.
  // Every set whose stored slot differs from where it's shown gets written too, so sets that
  // predate slots are pinned where they are. Applied to the cache first so the card lands without
  // waiting on the server, and rolled back by a refetch if any write fails.
  const place = async (set, slot, sets) => {
    const next = moveToSlot(placeWorkingSets(sets), set.id, slot);
    const changed = next
      .map((s, i) => (s && s.order !== i ? { set: s, order: i } : null))
      .filter(Boolean);
    if (changed.length === 0) return;

    const valueFor = ({ set: s, order }) => serializeWorkingSet({ icon: s.icon, colour: s.colour, order, filters: s.filters });
    await queryClient.cancelQueries({ queryKey: ["uiPreferences"] });
    patchCache((old) =>
      old.map((p) => {
        const hit = changed.find((c) => c.set.id === p.id);
        return hit ? { ...p, value: valueFor(hit) } : p;
      }),
    );
    const results = await Promise.all(
      changed.map((c) => updateUiPreference(c.set.id, { name: c.set.name, value: valueFor(c) }, { invalidate: false })),
    );
    if (results.some((ok) => !ok)) {
      toast({ description: "Couldn't move that working set. Please try again." });
      queryClient.invalidateQueries({ queryKey: ["uiPreferences"] });
    }
  };

  return { create, update, remove, duplicate, place };
};
